import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

import { BACKEND_URL, API } from '../config';


const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('cuet_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setUser(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, name, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        name,
        password
      });
      if (response.data.success) {
        const { token: newToken, user: newUser } = response.data.data;
        localStorage.setItem('cuet_token', newToken);
        setToken(newToken);
        setUser(newUser);
        return { success: true };
      }
      return { success: false, error: response.data.message };
    } catch (error) {
      const msg = error.response?.data?.detail || error.response?.data?.message || 'Registration failed. Please try again.';
      return { success: false, error: msg };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });
      if (response.data.success) {
        const { token: newToken, user: newUser } = response.data.data;
        localStorage.setItem('cuet_token', newToken);
        setToken(newToken);
        setUser(newUser);
        return { success: true };
      }
      return { success: false, error: response.data.message };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('cuet_token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const refreshUser = async () => {
    if (!token) return;
    await fetchUser();
  };

  /**
   * Set a JWT + user in one shot.
   * Used by guest-checkout (PaymentStatusPage) and magic-link login.
   */
  const setSession = (newToken, newUser) => {
    if (newToken) {
      localStorage.setItem('cuet_token', newToken);
      setToken(newToken);
    }
    if (newUser) {
      setUser(newUser);
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    refreshUser,
    setSession,
    isAuthenticated: !!user,
    isOnboarded: user?.preferences?.onboarding_completed
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
